import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import classNames from 'classnames';
import './MobileSlideUpPanel.scss';
import { IconCancel } from 'components/Icons';

interface MobileSlideUpPanelProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  title?: string; // Optional title for the panel
  titleIcon?: React.ReactNode; // Optional icon to display next to the title
  showGrabHandle?: boolean; // Default true
  showExplicitCloseButton?: boolean; // Default true
  showBetaBadge?: boolean; // Whether to show a BETA badge next to the title
  initialHeight?: number; // Initial height in pixels, default 300
  minHeight?: number; // Minimum height in pixels, default 150
  maxHeight?: number; // Maximum height in pixels, default 80% of viewport height
  resizable?: boolean; // Whether the panel is resizable, default true
  fullSizeOnOpen?: boolean; // Whether to open the panel at full size initially
  useFitContent?: boolean; // Whether to use min-height instead of fixed height, default true
}

export const MobileSlideUpPanel: React.FC<MobileSlideUpPanelProps> = React.memo(
  ({
    isOpen,
    onClose,
    children,
    title,
    titleIcon,
    showGrabHandle = true,
    showExplicitCloseButton = true,
    showBetaBadge = false,
    initialHeight = 300,
    minHeight = 150,
    maxHeight: propMaxHeight, // Will be calculated dynamically
    resizable = true,
    fullSizeOnOpen = false,
    useFitContent = true,
  }) => {
    // Dynamically calculate maxHeight based on window size
    const [calculatedMaxHeight, setCalculatedMaxHeight] = useState(window.innerHeight - 50);

    // Use provided maxHeight or calculated one
    const maxHeight = propMaxHeight ?? calculatedMaxHeight;
    // Track if component should be in DOM
    const [isInDOM, setIsInDOM] = useState(isOpen);
    // Track animation state separately from isOpen prop
    const [isAnimatingIn, setIsAnimatingIn] = useState(false);
    // Track panel height for resizing - use maxHeight if fullSizeOnOpen is true
    const [panelHeight, setPanelHeight] = useState(fullSizeOnOpen ? maxHeight : initialHeight);
    // Track if currently dragging
    const [isDragging, setIsDragging] = useState(false);
    // Store the starting point for drag calculations
    const [dragStartY, setDragStartY] = useState(0);
    const [startHeight, setStartHeight] = useState(initialHeight);
    // Track velocity for gesture detection
    const [lastY, setLastY] = useState(0);
    const [lastTime, setLastTime] = useState(0);
    const [velocity, setVelocity] = useState(0); // pixels per millisecond

    // Memoize panel content class to prevent unnecessary recalculations
    const panelContentClass = useMemo(() => {
      return classNames('MobileSlideUpPanel-content', {
        'is-open': isAnimatingIn,
        'is-dragging': isDragging,
      });
    }, [isAnimatingIn, isDragging]);

    // Initialize animation state based on isOpen prop
    useEffect(() => {
      if (isOpen) {
        setIsInDOM(true);
        // Use a small timeout to ensure the DOM has updated
        const timer = setTimeout(() => {
          setIsAnimatingIn(true);
        }, 10);
        return () => clearTimeout(timer);
      }
    }, []);

    // Reference to the panel content for direct DOM manipulation if needed
    const panelRef = useRef<HTMLDivElement>(null);

    // Handle window resize to update maxHeight
    useEffect(() => {
      const handleResize = () => {
        setCalculatedMaxHeight(window.innerHeight - 50);

        // If panel is currently taller than new max height, adjust it
        if (panelHeight > window.innerHeight - 50) {
          setPanelHeight(window.innerHeight - 50);
        }
      };

      window.addEventListener('resize', handleResize);
      window.addEventListener('orientationchange', handleResize);

      // Initial calculation
      handleResize();

      return () => {
        window.removeEventListener('resize', handleResize);
        window.removeEventListener('orientationchange', handleResize);
      };
    }, [panelHeight]);

    // Handle opening and closing animations
    useEffect(() => {
      // Skip the initial render since we handle that in the initialization effect
      if (isOpen) {
        // When opening, first add to DOM, then animate in
        setIsInDOM(true);
        // Use a more reliable approach with setTimeout instead of requestAnimationFrame
        const timer = setTimeout(() => {
          setIsAnimatingIn(true);
        }, 20); // Small delay to ensure the browser has painted the initial state
        return () => clearTimeout(timer);
      } else {
        // When closing, first animate out, then remove from DOM
        setIsAnimatingIn(false);

        // Reset panel height to initial height (or maxHeight if fullSizeOnOpen) when closing
        setPanelHeight(fullSizeOnOpen ? maxHeight : initialHeight);

        // Wait for animation to complete before removing from DOM
        const timer = setTimeout(() => {
          setIsInDOM(false);
        }, 300); // Match this with the CSS transition duration
        return () => clearTimeout(timer);
      }
    }, [isOpen, initialHeight]);

    const handleOverlayClick = useCallback(() => {
      onClose();
    }, [onClose]);

    // Handle touch events for dragging
    const handleTouchStart = useCallback(
      (e: React.TouchEvent) => {
        if (!resizable) return;

        try {
          if (e.touches.length === 0) return; // Guard against invalid touch events

          const touchY = e.touches[0].clientY;

          setIsDragging(true);
          setDragStartY(touchY);
          setStartHeight(panelHeight);

          // Initialize velocity tracking
          setLastY(touchY);
          setLastTime(Date.now());
          setVelocity(0);

          // Prevent default to avoid scrolling while dragging
          e.preventDefault();
        } catch (error) {
          console.error('Error in handleTouchStart:', error);
          // Recover from error by resetting drag state
          setIsDragging(false);
        }
      },
      [resizable, panelHeight]
    );

    const handleTouchMove = useCallback(
      (e: React.TouchEvent) => {
        if (!isDragging || !resizable) return;

        try {
          if (e.touches.length === 0) return; // Guard against invalid touch events

          const currentY = e.touches[0].clientY;
          const currentTime = Date.now();
          const deltaY = dragStartY - currentY;
          let newHeight = startHeight + deltaY;

          // Calculate velocity (pixels per millisecond)
          if (currentTime - lastTime > 0) {
            const instantVelocity = (lastY - currentY) / (currentTime - lastTime);
            // Use a weighted average to smooth out the velocity
            setVelocity(prev => prev * 0.7 + instantVelocity * 0.3);
          }

          // Update last position and time for next velocity calculation
          setLastY(currentY);
          setLastTime(currentTime);

          // Enforce min/max constraints
          newHeight = Math.max(minHeight, Math.min(maxHeight, newHeight));

          // Use requestAnimationFrame for smoother updates
          requestAnimationFrame(() => {
            setPanelHeight(newHeight);
          });

          // Prevent default to avoid scrolling while dragging
          e.preventDefault();
        } catch (error) {
          console.error('Error in handleTouchMove:', error);
          // Recover from error
          setIsDragging(false);
        }
      },
      [isDragging, resizable, dragStartY, startHeight, lastTime, lastY, minHeight, maxHeight]
    );

    const handleTouchEnd = useCallback(() => {
      if (!resizable) return;

      try {
        setIsDragging(false);

        // Detect fast gestures based on velocity
        const VELOCITY_THRESHOLD = 0.5; // pixels per millisecond

        if (velocity < -VELOCITY_THRESHOLD) {
          // Fast downward swipe - close the panel
          onClose();
          return;
        } else if (velocity > VELOCITY_THRESHOLD) {
          // Fast upward swipe - maximize the panel
          setPanelHeight(maxHeight);
          return;
        }

        // For slower gestures, use position-based logic

        // If dragged below initial height or below minimum threshold, close the panel
        if (panelHeight < initialHeight * 0.8 || panelHeight < minHeight * 0.7) {
          onClose();
        } else if (panelHeight > initialHeight * 1.5) {
          // If dragged significantly above initial height, snap to max height
          setPanelHeight(maxHeight);
        } else {
          // Otherwise, snap back to initial height
          setPanelHeight(initialHeight);
        }
      } catch (error) {
        console.error('Error in handleTouchEnd:', error);
        // Recover from error by resetting to initial height
        setPanelHeight(initialHeight);
      }
    }, [resizable, velocity, panelHeight, initialHeight, minHeight, maxHeight, onClose]);

    // For click handling (separate from drag)
    const handleGrabHandleClick = useCallback(
      (e: React.MouseEvent) => {
        // Only treat as a click if we're not at the end of a drag operation
        if (!isDragging) {
          onClose();
        }
        e.preventDefault();
      },
      [isDragging, onClose]
    );

    const handleExplicitCloseClick = useCallback(() => {
      onClose();
    }, [onClose]);

    // Prevent clicks inside the panel content from closing it via the overlay
    const handlePanelContentClick = useCallback((e: React.MouseEvent) => {
      e.stopPropagation();
    }, []);

    // Don't render anything if not in DOM
    if (!isInDOM) {
      return null;
    }

    return (
      <div
        className={classNames('MobileSlideUpPanel-overlay', { 'is-open': isAnimatingIn })}
        onClick={handleOverlayClick}
        role="dialog"
        aria-modal={isOpen ? 'true' : undefined}
        aria-labelledby={title ? 'slide-up-panel-title' : undefined}
        aria-describedby="mobile-slide-up-panel-description"
      >
        <div
          ref={panelRef}
          className={panelContentClass}
          onClick={handlePanelContentClick}
          style={{
            minHeight: useFitContent ? `${panelHeight}px` : `${minHeight}px`,
            maxHeight: `${maxHeight}px`,
          }}
        >
          {/* Hidden description for screen readers */}
          <div id="mobile-slide-up-panel-description" className="sr-only">
            This is a slide-up panel. Drag the handle at the top to resize or dismiss the panel.
          </div>
          {showGrabHandle && (
            <div
              className="MobileSlideUpPanel-grabHandle"
              onClick={handleGrabHandleClick}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            />
          )}
          {(title || titleIcon || showExplicitCloseButton) && (
            <div
              className={classNames('MobileSlideUpPanel-header', {
                'MobileSlideUpPanel-header--center': !showExplicitCloseButton,
              })}
            >
              <div className="MobileSlideUpPanel-titleContainer">
                {titleIcon && <div className="MobileSlideUpPanel-icon">{titleIcon}</div>}
                <div className="MobileSlideUpPanel-titleWrapper">
                  {title && (
                    <h3 id="slide-up-panel-title" className="MobileSlideUpPanel-title">
                      {title}
                    </h3>
                  )}
                  {showBetaBadge && <span className="MobileSlideUpPanel-betaBadge">BETA</span>}
                </div>
              </div>
              {showExplicitCloseButton && (
                <button
                  type="button"
                  className="MobileSlideUpPanel-closeButton"
                  onClick={handleExplicitCloseClick}
                  aria-label="Close panel"
                >
                  <IconCancel />
                </button>
              )}
            </div>
          )}
          <div className="MobileSlideUpPanel-body">{children}</div>
        </div>
      </div>
    );
  }
);
